// ─────────────────────────────────────────────────────────────────────────────
// email-body-anchor.util (WB-7625)
// ─────────────────────────────────────────────────────────────────────────────
// Resolves a `kind: 'text_range'` location — a verbatim quote plus a 1-indexed
// occurrence — to a DOM `Range` inside the rendered email body.
//
// A quote is the whole logical order line and regularly spans several physical
// lines, so no single element's text equals it: it has to be found as a
// substring and wrapped as a range. Finding a substring needs a string, and
// mapping the hit back to the DOM needs to know which text node produced each
// character — hence the index below.
//
// The emitter canonicalizes the same way. If the two transforms disagree by one
// character every occurrence index shifts, so the transform lives in one place
// (`canonicalizeBodyText`) and the walker is pinned against `innerText` by spec.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * U+0085 (NEL) has to be named explicitly: Python's `str.split()` folds it, JS `\s` does not, so a
 * NEL in forwarded mail would leave our string one character longer than the emitter's. U+FEFF is
 * the mirror case — JS folds it, Python does not — and the emitter strips it before canonicalizing.
 */
const WHITESPACE = /[\s\u0085]+/g;
const WHITESPACE_CHAR = /[\s\u0085]/;

/** Elements whose used display keeps text on the same rendered line, so it needs no separator. */
const INLINE_DISPLAY = /^(inline|contents|ruby)/;

/**
 * Collapse every run of whitespace to a single space, then trim. Case and punctuation untouched.
 *
 * The trim is not cosmetic: `text.split(/\s+/).join(' ')` alone yields empty leading/trailing
 * elements on a string with edge whitespace and so leaves a stray space at each end, which shifts
 * every offset against the emitter's `" ".join(text.split())`.
 */
export function canonicalizeBodyText(text: string | null | undefined): string {
  if (typeof text !== 'string') {
    return '';
  }
  return text.replace(WHITESPACE, ' ').trim();
}

/** One run of canonical characters that came from a single text node, contiguously. */
interface BodySegment {
  /** Offset of this run's first character in `BodyIndex.text`. */
  canonStart: number;
  node: Text;
  /** Offset in `node.data` of the character at `canonStart`. */
  nodeOffset: number;
  length: number;
}

export interface BodyIndex {
  /** Canonicalized rendered text of the walked subtree. Equal to `canonicalizeBodyText(innerText)`. */
  text: string;
  /** Provenance for every character in `text`, in order. */
  segments: BodySegment[];
}

function computedStyleOf(el: Element): CSSStyleDeclaration | null {
  try {
    return el.ownerDocument?.defaultView?.getComputedStyle(el) ?? null;
  } catch {
    return null;
  }
}

/**
 * Both checks are load-bearing: `innerText` is the reference string and it omits `display: none`
 * AND `visibility: hidden` subtrees (verified in Chrome by the walker-vs-innerText specs — dropping
 * the visibility half put the walker one run ahead of it).
 *
 * The emitter's HTML-to-text keeps hidden content (preheader divs), so it can see a quote we
 * cannot. Accepted: that direction loses a match, it never invents one.
 */
function isHiddenElement(el: Element): boolean {
  const style = computedStyleOf(el);
  return !!style && (style.display === 'none' || style.visibility === 'hidden');
}

/**
 * Unknown display (a detached subtree yields an empty computed style) counts as inline. That can
 * only merge two runs `innerText` would have separated, which loses a match — never invents one.
 */
function isInlineElement(el: Element): boolean {
  const display = (computedStyleOf(el)?.display ?? '').toLowerCase();
  return !display || INLINE_DISPLAY.test(display);
}

/**
 * Walk the subtree building the canonical string plus the map back to its text nodes.
 *
 * Never throws and returns an empty index for anything unusable — a renderer must not break on a
 * payload, and an empty index simply yields no highlight.
 */
export function buildBodyIndex(root: HTMLElement | null | undefined): BodyIndex {
  const doc = root?.ownerDocument;
  if (!root || !doc || typeof doc.createTreeWalker !== 'function') {
    return { text: '', segments: [] };
  }

  // One computed style per element rather than per text node: `blockAncestorOf` climbs the same
  // ancestors for every text node in a deep body.
  const inlineCache = new Map<Element, boolean>();
  const isInline = (el: Element): boolean => {
    let cached = inlineCache.get(el);
    if (cached === undefined) {
      cached = isInlineElement(el);
      inlineCache.set(el, cached);
    }
    return cached;
  };
  const blockAncestorOf = (node: Node): Element => {
    let el = node.parentElement;
    while (el && el !== root) {
      if (!isInline(el)) {
        return el;
      }
      el = el.parentElement;
    }
    return root;
  };

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node: Node) =>
      // Rejecting an element skips its whole subtree, which is exactly what hiding does.
      node.nodeType === Node.ELEMENT_NODE && isHiddenElement(node as Element)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });

  const segments: BodySegment[] = [];
  let text = '';
  let open: BodySegment | null = null;
  // Starts true so leading whitespace emits nothing — that IS the leading trim, applied inline.
  // Trimming the assembled string afterwards would shift every offset in the map.
  let lastWasSpace = true;
  let pendingSeparator = false;
  let sawBreak = false;
  let prevBlock: Element | null = null;

  const push = (ch: string, node: Text, nodeOffset: number): void => {
    if (open && open.node === node && open.nodeOffset + open.length === nodeOffset) {
      open.length += 1;
    } else {
      open = { canonStart: text.length, node, nodeOffset, length: 1 };
      segments.push(open);
    }
    text += ch;
  };

  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      if ((current as Element).tagName === 'BR') {
        sawBreak = true;
      }
      continue;
    }

    const node = current as Text;
    const block = blockAncestorOf(node);
    // `innerText` separates text that renders on different lines. BOTH triggers are needed: a
    // changed block ancestor catches `<td>A</td><td>B</td>` and also the way OUT of a nested block
    // (`<div>a<div>b</div>c</div>` -> "a b c", which an entering-a-block rule alone misses), while
    // a <br> leaves the ancestor untouched.
    if (sawBreak || (prevBlock !== null && block !== prevBlock)) {
      pendingSeparator = true;
    }
    sawBreak = false;
    prevBlock = block;

    const data = node.data;
    for (let i = 0; i < data.length; i += 1) {
      const ch = data[i];
      if (WHITESPACE_CHAR.test(ch)) {
        // A run spanning two text nodes ("foo  " + "  bar") collapses to the one space emitted
        // here, attributed to a real position — the case that breaks collapsing after stitching.
        if (!lastWasSpace) {
          push(' ', node, i);
          lastWasSpace = true;
        }
        pendingSeparator = false; // real whitespace already separates the two runs
        continue;
      }
      if (pendingSeparator) {
        if (!lastWasSpace) {
          push(' ', node, i);
        }
        pendingSeparator = false;
      }
      push(ch, node, i);
      lastWasSpace = false;
    }
  }

  // Trailing trim. Safe for the map because it is at the END — no offset before it moves.
  if (text.endsWith(' ')) {
    text = text.slice(0, -1);
    const last = segments[segments.length - 1];
    if (last) {
      last.length -= 1;
      if (last.length === 0) {
        segments.pop();
      }
    }
  }

  return { text, segments };
}

/** Segment containing `offset`, by binary search. */
function locate(segments: BodySegment[], offset: number): { node: Text; nodeOffset: number } | null {
  if (!segments.length || offset < 0) {
    return null;
  }
  let lo = 0;
  let hi = segments.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (segments[mid].canonStart <= offset) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  const seg = segments[lo];
  const within = offset - seg.canonStart;
  return within >= 0 && within < seg.length ? { node: seg.node, nodeOffset: seg.nodeOffset + within } : null;
}

function buildRange(index: BodyIndex, start: number, end: number): Range | null {
  const from = locate(index.segments, start);
  // Anchor the exclusive end on the match's LAST character, so the boundary sits immediately after
  // real text instead of before whatever happens to follow it in another node.
  const lastChar = locate(index.segments, end - 1);
  if (!from || !lastChar) {
    return null;
  }
  try {
    const range = from.node.ownerDocument!.createRange();
    range.setStart(from.node, from.nodeOffset);
    range.setEnd(lastChar.node, lastChar.nodeOffset + 1);
    return range.collapsed ? null : range;
  } catch {
    return null;
  }
}

/** Half-open `[start, end)` span of a hit, in `BodyIndex.text` coordinates. */
export interface QuoteOffsets {
  start: number;
  end: number;
}

/**
 * Offsets of `quote`'s `occurrence`-th hit in an already-built index, or `null` when it does not
 * resolve. Separate from `resolveQuoteRange` so N anchors can be located against ONE index — the
 * walk is the expensive part, and rebuilding it per anchor would also mean re-walking a DOM that
 * earlier anchors have already mutated.
 */
export function findQuoteOffsets(
  index: BodyIndex,
  quote: string | null | undefined,
  occurrence?: number | null,
): QuoteOffsets | null {
  // Canonicalize our copy of the quote too. The emitter sends canonical text, so this is normally a
  // no-op — but uncollapsed runs do occur upstream (a `raw_phrase` preserving three consecutive
  // spaces), and collapsing can only turn a silent miss into a hit, never a hit into a wrong one.
  const needle = canonicalizeBodyText(quote);
  if (!needle || !index?.text) {
    return null;
  }
  const want = occurrence === undefined || occurrence === null ? 1 : occurrence;
  if (!Number.isInteger(want) || want < 1) {
    return null; // a malformed index gets no highlight rather than a guessed one
  }

  let from = 0;
  let hits = 0;
  for (;;) {
    const at = index.text.indexOf(needle, from);
    if (at < 0) {
      return null;
    }
    hits += 1;
    if (hits === want) {
      return { start: at, end: at + needle.length };
    }
    // NON-overlapping. The emitter numbers the hits of this exact scan, so any other step would
    // land on a different hit than the one it counted.
    from = at + needle.length;
  }
}

/**
 * Map an offset span onto the DOM. Exported for the multi-anchor path, which has to capture every
 * anchor's nodes BEFORE it starts wrapping — see the directive.
 */
export function rangeForOffsets(index: BodyIndex, offsets: QuoteOffsets): Range | null {
  return buildRange(index, offsets.start, offsets.end);
}

/**
 * Find `quote`'s `occurrence`-th hit in the rendered text of `root` and return a `Range` over it,
 * or `null` when it does not resolve. No match means no highlight, silently — a forwarded or edited
 * body can legitimately not contain the row, and a guess would be worse than nothing.
 */
export function resolveQuoteRange(
  root: HTMLElement | null | undefined,
  quote: string | null | undefined,
  occurrence?: number | null,
): Range | null {
  if (!root) {
    return null;
  }
  const index = buildBodyIndex(root);
  const offsets = findQuoteOffsets(index, quote, occurrence);
  return offsets ? rangeForOffsets(index, offsets) : null;
}
